import {
  catchError,
  map,
  Observable,
  shareReplay,
  throwError,
  timeout,
} from 'rxjs';
import { Inject, Injectable, Injector } from '@angular/core';
import {
  DadosPdfResponse,
  OrcamentoByIdRequest,
  OrcamentoByIdResponse,
  OrcamentoRequest,
  OrcamentoResponse,
  PutStatus,
  ResponseBudget,
  ResponseLibera,
  ResquestBudget,
  TotalDeVendasResponse,
} from '../../../models/orcamento.models';
import { JwtTokenService, UtilsBaseApiService } from '@brunsker/libs/core/utils/base-api';
import { ENVIRONMENT_TOKEN, Environment } from '@brunsker/libs/core/utils/http-log-interceptor/src';
import { PaginatorResponse } from '../../../models/login.models';
import { TipoDeVendaItensWinthor } from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

@Injectable({
  providedIn: 'root',
})
export class NewApiOrcamentoService extends UtilsBaseApiService<NewApiOrcamentoService> {
  private readonly totalDeVendasCache = new Map<
    number,
    Observable<TotalDeVendasResponse[]>
  >();

  constructor(
    @Inject(ENVIRONMENT_TOKEN) readonly environment: Environment,
    override readonly injector: Injector,
    private tokenInject: JwtTokenService
  ) {
    super('orcamento', injector, environment.BASE_API_URL);
  }

  private socket$?: WebSocketSubject<any>;

  readonly GET_ORCAMENTOS_CONTEXT = 'search';
  readonly TOTAL_DE_VENDAS_CONTEXT = 'totalVendas';
  readonly TIPO_VENDA_7_CONTEXT = 'tipoVenda8';
  readonly GET_ORCAMENTO_BY_ID = 'get-orcamento-by-id';
  readonly TRANSFORMA_PEDIDO = 'transformaPedido';
  readonly LIBERA_ALL_ONE_ORCA = 'liberaAllOneOrca';
  readonly ITENS_CONTEXT = 'devolucaoTv7';
  readonly VALOR_ENTREGUE = 'valorEntregue';
  readonly AUTORIZACAO_CONTEXT = 'autorizacao';
  readonly STATUS_CONTEXT = 'status';
  readonly DADOS_PDF_CONTEXT = 'dados_pdf';
  readonly OBTER_SEQ_PEDIDO_CONTEXT = 'obter_seqPedido';

  getOrcamento(
    request: OrcamentoRequest
  ): Observable<PaginatorResponse<OrcamentoResponse>> {

    const dataIni = request.DATAINI?.replaceAll('/', '-')
    const dataFim = request.DATAFIM?.replaceAll('/', '-')

    let real_request: OrcamentoRequest = {
      linhaInicial: request.linhaInicial,
      intervalo: request.intervalo,
      pCODUSUR: request.pCODUSUR
    }

    if(request.ID_CLIENTE != "")
      real_request.ID_CLIENTE = request.ID_CLIENTE
    if(request.ID_VENDEDOR != "")
      real_request.ID_VENDEDOR = request.ID_VENDEDOR
    if(request.ID_FILIAL != "")
      real_request.ID_FILIAL = request.ID_FILIAL
    if(request.ORCAMENTO_FILIAL != "")
      real_request.ORCAMENTO_FILIAL = request.ORCAMENTO_FILIAL
    if(request.SEQ_PEDIDO != "")
      real_request.SEQ_PEDIDO = request.SEQ_PEDIDO
    if(request.STATUSPED != "")
      real_request.STATUSPED = request.STATUSPED
    if(dataIni)
      real_request.DATAINI = dataIni
    if(dataFim)
      real_request.DATAFIM = dataFim

      // if ( ( request.ID_CLIENTE?.length == 11|| request.ID_CLIENTE?.length == 14 )&& !isNaN( Number(request.ID_CLIENTE) ) ) {
      //   real_request.CPFCNPJ = request.ID_CLIENTE
      //   real_request.ID_CLIENTE = undefined;
      // }
    return this.httpClient.post<PaginatorResponse<OrcamentoResponse>>(
      `${this.BASE_URL}${this.CONTEXT}/${this.GET_ORCAMENTOS_CONTEXT}`,
      real_request
    );
  }

  getValoresEntrega(numeroOrcamento: number) {
    return this.httpClient.get<{
      vlentregue?: number;
      iD_ORCAMENTO?: number;
    }>(
      `${this.BASE_URL}${this.CONTEXT}/${this.ITENS_CONTEXT}/${this.VALOR_ENTREGUE}/${numeroOrcamento}`
    );
  }

  getTotalDeVendas(
    matricula: number,
    refresh = false,
  ): Observable<TotalDeVendasResponse[]> {
    const cachedResponse = this.totalDeVendasCache.get(matricula);
    if (cachedResponse && !refresh) return cachedResponse;

    const queryString = this.getQueryString({matricula});
    const response$ = this.httpClient
      .get<TotalDeVendasResponse[]>(
        `${this.BASE_URL}${this.CONTEXT}/${this.TOTAL_DE_VENDAS_CONTEXT}?${queryString}`,
      )
      .pipe(
        catchError((error: unknown) => {
          this.totalDeVendasCache.delete(matricula);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.totalDeVendasCache.set(matricula, response$);
    return response$;
  }

  getOrcamentoById(requestData: OrcamentoByIdRequest): Observable<OrcamentoByIdResponse> {
    const queryString = this.getQueryString(requestData);
    return (this.httpClient.get<OrcamentoByIdResponse>(
      `${this.BASE_URL}${this.CONTEXT}/get-orcamento-by-id?${queryString}`
      )).pipe(shareReplay(1));
  }

  getOrcamentoByIdSockt(requestData: OrcamentoByIdRequest): Observable<OrcamentoByIdResponse> {
    if(!this.socket$ || this.socket$.closed)
      this.socket$ = webSocket(`${this.BASE_URL.replace('http', 'ws')}wsOrca?Bearer=${this.tokenInject.getJwtToken()}`);
    this.socket$.next(requestData);
    return this.socket$.pipe(
      timeout({
        first: 4000,
        with: () => this.getOrcamentoById(requestData),
      }),
      catchError(() => this.getOrcamentoById(requestData)),
      map((orcamento: OrcamentoByIdResponse & { OBSERVACAO_PC?: string | null }) => ({
        ...orcamento,
        observacaO_PC: orcamento.observacaO_PC ?? orcamento.OBSERVACAO_PC,
      })),
      shareReplay(1)
    );
  }

  desconectSockt(): void
  {
    this.socket$?.complete();
    this.socket$ = undefined;
  }

  createOrcamento<T>(data: T) {
    return this.httpClient.post<number>(
      `${this.BASE_URL}${this.CONTEXT}`,
      data
    );
  }

  transformInOrder(seqPedido: number) {
    return this.httpClient.post<null>(
      `${this.environment.BASE_API_URL}${this.CONTEXT}/${this.TRANSFORMA_PEDIDO}`,
      { SEQ_PEDIDO: seqPedido}
    );
  }

  updateDiscountInOrcamento(data: {
    matricula: number;
    numorca: number;
    listanumautor: number[];
  }) {
    return this.httpClient.put<number>(
      `${this.BASE_URL}${this.CONTEXT}/${this.AUTORIZACAO_CONTEXT}`,
      data
    );
  }

  getTipoVenda7(request: {
    CodigoCliente?: number;
    NumeroPedido?: number;
    CodigoOrcamento?: number;
    DataInicial?: string;
    DataFinal?: string;
  }) {
    const queryString = this.getQueryString(request);

    return this.httpClient.get<
      {
        dT_FATURAMENTO: Date;
        dtinclusao: string;
        iD_COBRANCA: string;
        iD_ORCAMENTO: number;
        iD_PARCELA: string;
        numitens: number;
        seQ_PEDIDO: number;
        vlentregue: number;
        vltotal: number;
        status: string;
        idFilial: string;
      }[]
    >(
      `${this.BASE_URL}${this.CONTEXT}/${this.TIPO_VENDA_7_CONTEXT}?${queryString}`
    );
  }
  getTipoVenda7Itens(numeroOrcamento: number) {
    return this.httpClient.get<TipoDeVendaItensWinthor[]>(
      `${this.BASE_URL}${this.CONTEXT}/${this.ITENS_CONTEXT}/${numeroOrcamento}`
    );
  }

  updateOrcamento<T>(data: T) {
    return this.httpClient.put(`${this.BASE_URL}${this.CONTEXT}`, data);
  }

  updateStatusOrcamento(data: PutStatus) {
    return this.httpClient.put<string>(
      `${this.BASE_URL}${this.CONTEXT}/${this.STATUS_CONTEXT}`,
      data,
      { responseType: 'text' as 'json' }
    );
  }

  getDataPdf(id: number) {
    return this.httpClient.get<DadosPdfResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.DADOS_PDF_CONTEXT}?${this.getQueryString({idPedido: id})}`
    );
  }

  getSeqPedidoByTipoVenda() {
    return this.httpClient.get<
      Array<{
        seQ_PEDIDO: number;
      }>
    >(`${this.BASE_URL}${this.CONTEXT}/${this.OBTER_SEQ_PEDIDO_CONTEXT}`);
  }

  calculoOrcamento(data: ResquestBudget) {
    return this.httpClient.post<ResponseBudget>
    (`${this.BASE_URL}carrinho/calcule-carrinho`,data);
  }

  liberaAllOneOrca(data: {
    pIDVENDEDOR: number;
    pSEQPEDIDO?: number;
  }) {
    return this.httpClient.put<ResponseLibera>(`${this.BASE_URL}${this.CONTEXT}/liberaAllOneOrca`,data);
  }

  retrySincronizacao(seq_pedido:number) {
    return this.httpClient.put(`${this.BASE_URL}${this.CONTEXT}/sincronizar`,
      {
        pSEQPEDIDO: seq_pedido
      }

    );
  }
}
